import React, { useState, useEffect } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from './ui/sheet';
import { Separator } from './ui/separator';
import {
  ExternalLink, MapPin, Tag,
  Calendar, Package, Star, Navigation,
  Languages, Loader, Flag, AlertTriangle,
  Bookmark, Sparkles,
} from 'lucide-react';
import { api } from '../lib/api';
import { useTranslation } from 'react-i18next';
import {
  formatPrice, getSourceBadgeClass, getSourceName, getTranslatedCondition, getSessionId,
  getCategoryLabel,
} from '../lib/utils';
import { getDistanceFromUser } from '../lib/geolocation';
import { useAuth } from '../context/AuthContext';
import { useBrowsing } from '../context/BrowsingContext';
import { useProUpsell } from '../context/ProUpsellContext';
import { toast } from 'sonner';

// Extracted sub-components
import {
  formatDate, formatDateShort, SOURCE_COLORS,
  ListingImage, MetaRow, ShareButtons,
  PriceWatchForm, SimilarListings, PriceHistoryChart,
} from './quickview/QuickViewParts';
import { AssistantListingBox } from './AssistantListingBox';
import { AuctionReminderButton } from './AuctionReminderButton';
import { Mascot } from './Mascot';
import { useBackToClose } from '../hooks/useBackToClose';
import { FullscreenGallery } from './quickview/FullscreenGallery';

// ─── Main component ──────────────────────────────────────────────────────────
export function ListingQuickView({ listing, open, onOpenChange, userLocation, prefetchedImages = null, onLoginRequest = null, onUnavailableConfirmed = null, manageHistory = true }) {
  const { i18n, t } = useTranslation();
  useBackToClose(open, onOpenChange, manageHistory);
  const { user } = useAuth() || {};
  const browsing = useBrowsing();
  const { showProUpsell } = useProUpsell();
  const currentLang = i18n.language.split('-')[0];

  const [priceHistory, setPriceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [allImages, setAllImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [currentImgIdx, setCurrentImgIdx] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [hasReported, setHasReported] = useState(false);
  const [saved, setSaved] = useState(false);
  const [similarListings, setSimilarListings] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [priceWatchThreshold, setPriceWatchThreshold] = useState('');
  const [priceWatchEmail, setPriceWatchEmail] = useState('');
  const [priceWatchSet, setPriceWatchSet] = useState(false);
  const [priceWatchLoading, setPriceWatchLoading] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [showTranslated, setShowTranslated] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [ebayDescription, setEbayDescription] = useState(null);
  const [fetchedDescription, setFetchedDescription] = useState(null);
  const [yearContext, setYearContext] = useState(null);

  const baseDesc = (listing?.source === 'ebay' && ebayDescription) ? ebayDescription
    : (fetchedDescription || listing?.description);
  const displayTitle = showTranslated && translated ? translated.title : listing?.title;
  const displayDesc  = showTranslated && translated ? translated.description : baseDesc;

  const handleTranslate = async () => {
    if (translated) { setShowTranslated(v => !v); return; }
    setTranslating(true);
    try {
      const d = await api.translateListing(listing.source, listing.source_listing_id, currentLang);
      setTranslated({ title: d.title, description: d.description });
      setShowTranslated(true);
    } catch {} finally { setTranslating(false); }
  };

  const translateBtnLabel = showTranslated ? t('original') : `${t('translateTo')} ${currentLang.toUpperCase()}`;

  useEffect(() => {
    if (!open || !listing) return;
    // Le risposte di un annuncio aperto prima non devono finire su questo
    // (prima comparivano foto di altri annunci)
    let alive = true;
    const live = (fn) => (arg) => { if (alive) fn(arg); };
    setCurrentImgIdx(0); setGalleryOpen(false); setTranslated(null); setShowTranslated(false);
    setHasReported(false); setAvailabilityStatus(null); setSaved(false);
    setSimilarListings([]); setPriceWatchSet(false); setPriceWatchThreshold(''); setPriceWatchEmail('');
    setEbayDescription(null);
    setFetchedDescription(null);
    setYearContext(null);

    // Confronto per annata: "questo è un 2017, i 2023 stanno a CHF X"
    if (listing.title) {
      api.getYearContext(listing.title)
        .then(d => setYearContext(d?.available ? d : null))
        .catch(() => {});
    }

    // eBay description enrichment on-demand (Browse API, gratis, cache eterno backend)
    if (listing.source === 'ebay' && listing.source_listing_id) {
      api.getEbayEnrichment(listing.source_listing_id)
        .then(d => { if (d?.description) setEbayDescription(d.description); })
        .catch(() => {});
    }

    // Check Pro status
    if (user) {
      api.getProStatus(getSessionId()).then(d => setIsPro(d.is_pro === true)).catch(() => setIsPro(false));
    } else {
      setIsPro(false);
    }

    // Availability check
    if (!listing.sold) {
      setAvailabilityStatus('checking');
      api.checkAvailability(listing.source, listing.source_listing_id)
        .then(live(d => {
          if (!d.is_available) {
            setAvailabilityStatus('unavailable');
            // User feedback: prima il banner "Annuncio non disponibile" era
            // visibile, ma la card sottostante veniva marcata come venduta
            // istantaneamente — l'utente non aveva tempo di leggere. Aspettiamo
            // 2.5s prima di notificare il parent, così il banner rimane in
            // primo piano abbastanza a lungo per essere percepito.
            setTimeout(() => { onUnavailableConfirmed?.(); }, 2500);
          }
          else if (d.warning || d.blocked) { setAvailabilityStatus('warning'); }
          else { setAvailabilityStatus('available'); }
        }))
        .catch(live(() => setAvailabilityStatus(null)));
    }

    // Images
    // Ricardo: la descrizione vive solo nella pagina di dettaglio, e arriva
    // insieme alle immagini (stessa fetch) → la chiediamo anche se le immagini
    // sono già in cache locale.
    if (prefetchedImages && prefetchedImages.length > 1) {
      setAllImages(prefetchedImages); setImagesLoading(false);
      if (listing.source === 'ricardo' && !listing.description?.includes('\n')) {
        api.getListingImages(listing.source, listing.source_listing_id)
          .then(live(d => { if (d?.description) setFetchedDescription(d.description); }))
          .catch(() => {});
      }
    } else {
      setAllImages(listing.image_url ? [listing.image_url] : []);
      setImagesLoading(true);
      api.getListingImages(listing.source, listing.source_listing_id)
        .then(live(d => {
          const imgs = (d.images || []).filter(Boolean);
          if (imgs.length > 0) setAllImages(imgs);
          if (d?.description) setFetchedDescription(d.description);
        }))
        .catch(() => {}).finally(live(() => setImagesLoading(false)));
    }

    // Price history (Pro: il backend risponde 403 ai non Pro)
    setHistoryLoading(true);
    (user ? api.getPriceHistory(listing.source, listing.source_listing_id) : Promise.resolve({ history: [] }))
      .then(d => {
        const pts = (d.history || []).filter(h => h.price_chf != null).map(h => ({
          price_chf: h.price_chf, date: formatDateShort(h.recorded_at), fullDate: formatDate(h.recorded_at),
        }));
        setPriceHistory(pts);
      })
      .catch(() => setPriceHistory([])).finally(() => setHistoryLoading(false));

    // Similar listings
    setSimilarLoading(true);
    api.getSimilarListings(listing.source, listing.source_listing_id, 8)
      .then(live(d => setSimilarListings(d.similar || [])))
      .catch(live(() => setSimilarListings([]))).finally(live(() => setSimilarLoading(false)));
    return () => { alive = false; };
  // Per chiave, non per oggetto: un refresh della lista crea un nuovo oggetto
  // per lo stesso annuncio e resettava la galleria alla sola prima foto.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listing?.source, listing?.source_listing_id]);

  const galleryImages = allImages.length ? allImages : (listing?.image_url ? [listing.image_url] : []);
  const handlePrev = () => setCurrentImgIdx(i => (i - 1 + allImages.length) % allImages.length);
  const handleNext = () => setCurrentImgIdx(i => (i + 1) % allImages.length);
  const handleExternalClick = () => { if (browsing && listing) browsing.trackExternalClick(listing); };

  const handleSave = async () => {
    try {
      if (saved) { await api.deleteSavedListing(listing.source, listing.source_listing_id); setSaved(false); toast.success(t('removedFromSaved')); }
      else { await api.saveListing(listing); setSaved(true); toast.success(t('listingSaved')); }
    } catch (err) {
      if (err?.status === 403 || err?.code === 'PRO_REQUIRED') showProUpsell('saved');
      else toast.error(t('error'));
    }
  };

  const handlePriceWatch = async (e) => {
    e.preventDefault();
    const threshold = parseFloat(priceWatchThreshold);
    if (!threshold || threshold <= 0) { toast.error(t('invalidPrice')); return; }
    setPriceWatchLoading(true);
    try {
      const { getSessionId } = await import('../lib/utils');
      await api.createPriceWatch({
        session_id: getSessionId(), source: listing.source, source_listing_id: listing.source_listing_id,
        threshold_price: threshold, email: priceWatchEmail || undefined,
        title: listing.title, current_price: listing.price_chf, image_url: listing.image_url, listing_url: listing.listing_url,
      });
      setPriceWatchSet(true);
      toast.success(t('priceWatchSet', { price: threshold }));
    } catch { toast.error(t('priceWatchError')); }
    finally { setPriceWatchLoading(false); }
  };

  if (!listing) return null;

  const hasDeal = listing.deal_score && listing.deal_score >= 10;
  const sourceColor = SOURCE_COLORS[listing.source] || 'hsl(var(--foreground))';
  const distance = userLocation && listing.postal_code ? getDistanceFromUser(userLocation.lat, userLocation.lng, listing.postal_code) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Mobile: schermo intero. Desktop: finestra al centro (l'occhio non deve andare a destra). */}
      <SheetContent side="right" data-testid="quick-view-sheet" aria-describedby={undefined}
        className="w-full overflow-y-auto p-0 flex flex-col sm:inset-auto sm:left-1/2 sm:top-1/2 sm:right-auto sm:-translate-x-1/2 sm:-translate-y-1/2 sm:h-[min(92vh,940px)] sm:w-[min(94vw,720px)] sm:max-w-none sm:rounded-2xl sm:border sm:shadow-2xl sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%] sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:fade-in-0 sm:data-[state=closed]:fade-out-0 sm:duration-200">

        {/* Header */}
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <span data-testid="quick-view-source-badge" className={`text-xs px-2.5 py-1 rounded-full font-semibold ${getSourceBadgeClass(listing.source)}`}>
              {getSourceName(listing.source)}
            </span>
          </div>
          <SheetTitle data-testid="quick-view-title" className="text-base font-semibold leading-snug text-left">
            {displayTitle}
          </SheetTitle>
        </SheetHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 pt-4 space-y-5 pb-24">

            {/* Availability banner */}
            {availabilityStatus === 'checking' && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted text-muted-foreground text-xs">
                <Loader size={12} className="animate-spin shrink-0" /> {t('checkingAvailability')}
              </div>
            )}
            {availabilityStatus === 'unavailable' && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/25 text-orange-700 dark:text-orange-400 text-sm font-medium" data-testid="quickview-unavailable">
                <Mascot pose="confused" size={44} className="shrink-0 mx-0" /> {t('listingUnavailable')}
              </div>
            )}
            {availabilityStatus === 'warning' && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-yellow-500/10 border border-yellow-500/25 text-yellow-700 dark:text-yellow-400 text-xs">
                <AlertTriangle size={14} className="shrink-0" /> {t('availabilityWarning')}
              </div>
            )}

            {/* Image gallery */}
            <div className="relative">
              <ListingImage listing={listing} allImages={allImages} currentIdx={currentImgIdx} onPrev={handlePrev} onNext={handleNext}
                onOpen={galleryImages.length ? () => setGalleryOpen(true) : undefined} />
              {galleryImages.length > 0 && (
                <FullscreenGallery images={galleryImages} idx={Math.min(currentImgIdx, galleryImages.length - 1)} setIdx={setCurrentImgIdx}
                  open={galleryOpen} onOpenChange={setGalleryOpen} title={listing?.title} />
              )}
              {imagesLoading && allImages.length <= 1 && (
                <div
                  data-testid="quick-view-photos-loading"
                  className="absolute bottom-2 left-2 flex items-center gap-1.5 text-white text-xs px-2.5 py-1 rounded-full pointer-events-none"
                  style={{ background: 'rgba(0,0,0,0.65)' }}
                >
                  <Loader size={11} className="animate-spin" /> {t('loadingPhotos')}
                </div>
              )}
            </div>

            {/* Price + CTA */}
            <div className="flex items-center justify-between gap-3">
              <div>
                {(listing.is_auction || listing.sale_type === 'Auktion') && (
                  <p className="text-xs text-muted-foreground mb-0.5">{t('currentBid')}</p>
                )}
                <p className="text-3xl font-bold tabular-nums" data-testid="quick-view-price">{formatPrice(listing.price_chf, (k) => k)}</p>
                {(listing.is_auction || listing.sale_type === 'Auktion') && listing.buy_now_price > listing.price_chf && (
                  <p data-testid="quick-view-buy-now-price" className="text-sm text-muted-foreground mt-0.5">
                    {t('buyNowLabel')}: {formatPrice(listing.buy_now_price, (k) => k)}
                  </p>
                )}
                {/* Auction countdown — visible only for auctions with end_at */}
                {(listing.is_auction || listing.sale_type === 'Auktion') && listing.end_at && (() => {
                  const endDate = new Date(listing.end_at);
                  const remaining = endDate - new Date();
                  if (remaining <= 0) return null;
                  const mins = Math.floor(remaining / 60000);
                  const hours = Math.floor(mins / 60);
                  const days = Math.floor(hours / 24);
                  const urgent = remaining < 3600 * 1000;
                  const label = days > 0 ? `${days}g ${hours % 24}h` : hours > 0 ? `${hours}h ${mins % 60}m` : `${mins}m`;
                  const fullDate = endDate.toLocaleString('it-CH', { dateStyle: 'medium', timeStyle: 'short' });
                  return (
                    <p data-testid="quick-view-auction-countdown" className={`text-xs mt-1 flex items-center gap-1 font-semibold ${urgent ? 'text-red-500' : 'text-amber-600 dark:text-amber-400'}`} title={fullDate}>
                      <AlertTriangle size={12} />{t('endsIn') || 'Scade tra'} <span className="tabular-nums">{label}</span>
                      <span className="text-muted-foreground/80 font-normal ml-1">· {fullDate}</span>
                    </p>
                  );
                })()}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <AuctionReminderButton listing={listing} variant="full" onLoginRequest={onLoginRequest} />
                <button data-testid="quick-view-save-button" onClick={handleSave}
                  aria-label={saved ? t('savedListing') : t('saveListing')}
                  className={`flex items-center gap-1.5 px-3 sm:px-3 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95 ${saved ? 'bg-amber-500/15 text-amber-600 border border-amber-500/30' : 'bg-muted text-foreground hover:bg-muted/80 border border-border/60'}`}>
                  <Bookmark size={15} fill={saved ? 'currentColor' : 'none'} /> <span className="hidden sm:inline">{saved ? t('savedListing') : t('saveListing')}</span>
                </button>
                <a href={listing.listing_url} target="_blank" rel="noopener noreferrer" data-testid="quick-view-open-link" onClick={handleExternalClick}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-sm font-semibold text-background transition-opacity hover:opacity-90 active:scale-95 whitespace-nowrap" style={{ background: sourceColor }}>
                  <ExternalLink size={15} /> <span className="hidden sm:inline">{t('openOn')} {getSourceName(listing.source)}</span><span className="sm:hidden">{t('open')}</span>
                </a>
              </div>
            </div>

            {/* Assistente Pro: "ci sta o no?" + messaggio per trattare */}
            <AssistantListingBox listing={listing} />


            {/* Market price context */}
            {/* Media di riferimento: nascosta se il confronto non e' affidabile */}
            {listing.estimated_value && listing.estimated_value > 0 && listing.price_chf > 0 && listing.deal_reliability !== 'unverified' && (
              <div data-testid="quick-view-market-price" className={`rounded-xl px-4 py-3 border ${hasDeal ? 'bg-emerald-500/8 border-emerald-500/20' : 'bg-muted/30 border-border/40'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground mb-0.5">{t('avgPriceForSimilar')}</p>
                    <p className="text-lg font-bold tabular-nums">CHF {listing.estimated_value?.toLocaleString('it-CH')}</p>
                    {listing.price_window_days > 90 && (
                      <p data-testid="quick-view-price-window" className="text-[11px] text-muted-foreground mt-0.5">
                        {listing.price_window_season ? t('priceWindowSeason') : t('priceWindowLong', { months: Math.round(listing.price_window_days / 30) })}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    {listing.deal_score > 0 ? (
                      <div className={`text-right ${listing.deal_score >= 15 ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'}`}>
                        <p className="text-2xl font-bold tabular-nums">-{Math.round(listing.deal_score)}%</p>
                        <p className="text-xs opacity-70">{t('belowAverage')}</p>
                      </div>
                    ) : listing.price_chf > listing.estimated_value ? (
                      <div className="text-right text-muted-foreground">
                        <p className="text-sm font-medium">+{Math.round(((listing.price_chf - listing.estimated_value) / listing.estimated_value) * 100)}% {t('aboveAverage')}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
                {listing.n_comparables && <p className="text-xs text-muted-foreground mt-1.5">{t('basedOnComparables', { count: listing.n_comparables })}</p>}
              </div>
            )}

            {/* Annata: un 2020 non vale come un 2025 */}
            {yearContext && (
              <div data-testid="quick-view-year-context"
                className="rounded-xl px-4 py-3 border border-amber-500/20 bg-amber-500/8">
                <p className="text-xs text-muted-foreground mb-1">{t('yearContextLabel')}</p>
                <p className="text-sm">
                  {t('yearContextText', {
                    year: yearContext.year,
                    median: yearContext.median?.toLocaleString('it-CH'),
                    latestYear: yearContext.latest_year,
                    latestMedian: yearContext.latest_median?.toLocaleString('it-CH'),
                  })}
                </p>
              </div>
            )}

            {/* Share */}
            <div className="flex items-center justify-between gap-2 flex-wrap"><ShareButtons listing={listing} /></div>

            <Separator />

            {/* Description + translate */}
            {(displayDesc || listing.description) && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('description')}
                    {showTranslated && <span className="ml-2 text-blue-500 normal-case font-normal lowercase">· {t('translated')}</span>}
                  </h3>
                  <button onClick={handleTranslate} disabled={translating}
                    className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border border-border/60 hover:bg-muted transition-colors disabled:opacity-50 text-muted-foreground hover:text-foreground">
                    {translating ? <Loader size={11} className="animate-spin" /> : <Languages size={11} />} {translateBtnLabel}
                  </button>
                </div>
                <p data-testid="quick-view-description" className="text-sm leading-relaxed text-foreground/80 whitespace-pre-line">{displayDesc || listing.description}</p>
              </div>
            )}

            {/* Details */}
            <div>
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{t('detailsSection')}</h3>
              <div className="bg-muted/30 rounded-xl px-3 py-1">
                <MetaRow icon={MapPin} label={t('positionLabel')} value={[listing.city, listing.canton].filter(Boolean).join(', ') || listing.city} />
                {distance !== null && <MetaRow icon={Navigation} label={t('distance')} value={`${distance} km`} valueClass="text-blue-600 dark:text-blue-400" />}
                {listing.postal_code && <MetaRow icon={MapPin} label={t('postalCode')} value={listing.postal_code} />}
                <MetaRow icon={Calendar} label={t('publishedAt')} value={listing.posted_at ? formatDate(listing.posted_at) : listing.scraped_at ? `${t('detectedAt')} ${formatDate(listing.scraped_at)}` : null} />
                {listing.condition && !['pets', 'realEstate'].includes(listing.category) && <MetaRow icon={Star} label={t('conditionLabel')} value={getTranslatedCondition(listing.condition, t)}
                  valueClass={listing.condition_level >= 4 ? 'text-emerald-600 dark:text-emerald-400' : listing.condition_level >= 3 ? 'text-blue-600 dark:text-blue-400' : listing.condition_level >= 2 ? 'text-foreground' : listing.condition_level >= 1 ? 'text-amber-600' : listing.condition_level === 0 ? 'text-rose-500' : ''} />}
                {listing.brand && <MetaRow icon={Package} label={t('brandLabel')} value={listing.brand} />}
                {listing.sale_type && <MetaRow icon={Tag} label={t('saleTypeLabel')} value={listing.sale_type} />}
                {listing.category && <MetaRow icon={Tag} label={t('categoryLabel')} value={getCategoryLabel(listing.category, currentLang)} />}
              </div>
            </div>

            {/* Price History Chart — SOLO Pro */}
            {isPro ? (
              <PriceHistoryChart priceHistory={priceHistory} historyLoading={historyLoading} sourceColor={sourceColor} currentPrice={listing.price_chf} />
            ) : (
              <div
                className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/30 dark:to-slate-950 p-4 cursor-pointer hover:border-amber-300 transition-colors"
                onClick={() => showProUpsell('price_history')}
                data-testid="price-history-pro-upsell"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-foreground">{t('priceHistoryProTitle')}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{t('priceHistoryProDesc')}</div>
                    <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400 hover:text-amber-800">
                      {t('discoverPro')} →
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Price Watch — Pro only, below price history */}
            {isPro && (
              <PriceWatchForm listing={listing} priceWatchThreshold={priceWatchThreshold} setPriceWatchThreshold={setPriceWatchThreshold}
                priceWatchEmail={priceWatchEmail} setPriceWatchEmail={setPriceWatchEmail} priceWatchSet={priceWatchSet}
                priceWatchLoading={priceWatchLoading} onSubmit={handlePriceWatch} />
            )}

            {/* Similar Listings */}
            <SimilarListings similarListings={similarListings} similarLoading={similarLoading} />
          </div>
        </div>

        {/* Footer sticky */}
        <div className="absolute bottom-0 left-0 right-0 px-5 py-4 bg-background/95 backdrop-blur-sm border-t border-border/50">
          <a href={listing.listing_url} target="_blank" rel="noopener noreferrer" data-testid="quick-view-footer-cta" onClick={handleExternalClick}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold text-background transition-opacity hover:opacity-90 active:scale-[0.99]" style={{ background: sourceColor }}>
            <ExternalLink size={16} /> {t('goToListingOn')} {getSourceName(listing.source)}
          </a>
          <button data-testid="quick-view-report-button"
            onClick={async () => {
              if (!user) { toast.error(t('loginToReport'), { action: { label: t('signIn'), onClick: () => onLoginRequest?.() } }); return; }
              if (hasReported) return;
              try {
                const result = await api.reportListing(listing.source, listing.source_listing_id);
                if (result.reason === 'already_reported') toast.info(t('alreadyReported'));
                else { setHasReported(true); toast.success(t('thanksReport')); }
              } catch (err) {
                if (err?.status === 401) toast.error(t('loginToReport'), { action: { label: t('signIn'), onClick: () => onLoginRequest?.() } });
                else toast.error(t('reportError'));
              }
            }}
            className={`flex items-center justify-center gap-1.5 w-full mt-2 py-1.5 text-xs rounded-lg transition-colors ${hasReported ? 'text-orange-500 cursor-default' : 'text-muted-foreground hover:text-orange-500'}`}>
            <Flag size={11} /> {hasReported ? t('reportSent') : t('reportUnavailable')}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
